import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, finalize, of, switchMap, tap } from 'rxjs'
import { DefaultSkillMapTarget } from '../../../../_generated/graphql'
import {
  SelectComponent,
  Option,
} from '../../../components/select/select.component'
import { MessageDialogService } from '../../../components/message-dialog/message-dialog.service'
import {
  ASSESSMENT_SETTING_SECTION_LABELS,
  ASSESSMENT_SETTING_SECTION_DESCRIPTIONS,
  getVisibleAssessmentSettingSections,
} from '../../../config/assessment-settings.config'
import { SkillMap } from '../../../services/assessment/assessment.mapping'
import { UserService } from '../../../services/user/user.service'
import { AssessmentStore } from '../../../stores/assessment.store'
import { UserStore } from '../../../stores/user.store'
import { getTitle } from '../../../util/accessibility'
import { captureException } from '../../../util/captureException'
import { RoleModelRecommendationSettingComponent } from './role-model-recommendation-setting/role-model-recommendation-setting.component'

@UntilDestroy()
@Component({
  selector: 'app-assessment-settings-page',
  standalone: true,
  imports: [SelectComponent, RoleModelRecommendationSettingComponent],
  templateUrl: './assessment-settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './assessment-settings-page.component.scss',
})
export class AssessmentSettingsPageComponent implements OnInit {
  readonly labels = ASSESSMENT_SETTING_SECTION_LABELS
  readonly descriptions = ASSESSMENT_SETTING_SECTION_DESCRIPTIONS
  readonly unsetOption: Option = {
    id: '',
    name: '未設定(作成日時が最新のスキルマップ)',
  }
  sections: DefaultSkillMapTarget[] = []
  options: Option[] = [this.unsetOption]
  selectedOptions: Partial<Record<DefaultSkillMapTarget, Option>> = {}
  isSuperAdmin = false
  saving = false
  loading = true

  constructor(
    private assessmentStore: AssessmentStore,
    private userStore: UserStore,
    private userService: UserService,
    private messageDialogService: MessageDialogService,
    private snackBar: MatSnackBar,
    title: Title,
  ) {
    title.setTitle(getTitle('アセスメント設定'))
  }

  ngOnInit(): void {
    this.userStore
      .getOneSelf({ useCache: true })
      .pipe(
        tap((user) => {
          this.isSuperAdmin = user.roles.some(
            (role) => role.code === 'SUPER_ADMIN',
          )
          this.sections = getVisibleAssessmentSettingSections(
            user.enabledFeatures,
          )
        }),
        switchMap(() => this.assessmentStore.getSkillMaps()),
        untilDestroyed(this),
        finalize(() => {
          this.loading = false
        }),
      )
      .subscribe({
        next: (skillMaps) => this.syncSkillMaps(skillMaps),
        error: (error) =>
          this.showError(error, 'アセスメント設定の取得に失敗しました。'),
      })
  }

  onSelectionChange(target: DefaultSkillMapTarget, option: Option): void {
    const previous = this.selectedOptions[target] ?? this.unsetOption
    if (this.saving || previous.id === option.id) return
    this.selectedOptions[target] = option
    this.saving = true
    this.userService
      .updateDefaultSkillMap(target, option.id || null)
      .pipe(
        tap(() => {
          this.snackBar.open('初期選択スキルマップを更新しました。', 'close', {
            duration: 3000,
          })
        }),
        switchMap(() =>
          this.assessmentStore.getSkillMaps().pipe(
            tap((skillMaps) => this.syncSkillMaps(skillMaps)),
            catchError((error) => {
              this.showError(
                error,
                '表示の更新に失敗しました。時間を置いて再度お試しください。',
              )
              return of(null)
            }),
          ),
        ),
        catchError((error) => {
          this.selectedOptions[target] = { ...previous }
          this.showError(
            error,
            '初期選択スキルマップの保存に失敗しました。時間を置いて再度お試しください。',
          )
          return of(null)
        }),
        untilDestroyed(this),
        finalize(() => {
          this.saving = false
        }),
      )
      .subscribe()
  }

  private syncSkillMaps(skillMaps: SkillMap[]): void {
    this.options = [
      this.unsetOption,
      ...skillMaps.map(({ id, name }) => ({ id, name })),
    ]
    for (const target of this.sections) {
      const selected = skillMaps.find((map) =>
        map.defaultTargets.includes(target),
      )
      this.selectedOptions[target] =
        this.options.find((option) => option.id === selected?.id) ??
        this.unsetOption
    }
  }

  private showError(error: unknown, message: string): void {
    captureException(error)
    this.messageDialogService.showError(message, { title: 'エラー' })
  }
}
